import { supabase } from "./supabase";

/**
 * Interface for mind map data
 */
export interface MindMap {
  id: string;
  title: string;
  description: string | null;
  content: any; // JSON content from Excalidraw
  story_id: string;
  created_at: string;
  updated_at: string;
  tenant_id: string;
}

/**
 * Interface for creating a new mind map
 */
export interface CreateMindMapDTO {
  title: string;
  description?: string;
  content?: any;
  story_id: string;
}

/**
 * Interface for updating a mind map
 */
export interface UpdateMindMapDTO {
  title?: string;
  description?: string;
  content?: any;
}

/**
 * Get all mind maps for a specific story
 */
export const getMindMapsByStory = async (storyId: string): Promise<MindMap[]> => {
  const { data, error } = await supabase
    .from("mind_maps")
    .select("*")
    .eq("story_id", storyId)
    .order("updated_at", { ascending: false });

  if (error) {
    console.error(`Error fetching mind maps for story ${storyId}:`, error);
    throw error;
  }

  return data as MindMap[];
};

/**
 * Get a specific mind map by ID
 */
export const getMindMapById = async (id: string): Promise<MindMap | null> => {
  const { data, error } = await supabase
    .from("mind_maps")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error(`Error fetching mind map ${id}:`, error);
    throw error;
  }

  return data as MindMap;
};

/**
 * Create a new mind map
 */
export const createMindMap = async (mindMap: CreateMindMapDTO): Promise<MindMap> => {
  const { data, error } = await supabase
    .from("mind_maps")
    .insert([
      {
        title: mindMap.title,
        description: mindMap.description || null,
        content: mindMap.content || {},
        story_id: mindMap.story_id,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Error creating mind map:", error);
    throw error;
  }

  return data as MindMap;
};

/**
 * Update an existing mind map
 */
export const updateMindMap = async (id: string, updates: UpdateMindMapDTO): Promise<MindMap> => {
  const { data, error } = await supabase
    .from("mind_maps")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error(`Error updating mind map ${id}:`, error);
    throw error;
  }

  return data as MindMap;
};

/**
 * Delete a mind map
 */
export const deleteMindMap = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("mind_maps")
    .delete()
    .eq("id", id);

  if (error) {
    console.error(`Error deleting mind map ${id}:`, error);
    throw error;
  }
}; 